import type { CSSProperties } from 'react';
import { ThemePalette } from '../theme';

export const MODAL_STYLES = {
  content: {
    background: 'var(--ce-modal-bg)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    boxShadow: '0 24px 64px rgba(0,0,0,0.60)',
  },
  header: {
    background: 'transparent',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    paddingBottom: 16,
    color: '#fff',
  },
  body: {
    paddingTop: 20,
  },
  footer: {
    background: 'transparent',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    paddingTop: 16,
  },
  mask: {
    backdropFilter: 'blur(4px)',
    background: 'rgba(0,0,0,0.60)',
  },
};

export const DRAWER_STYLES = {
  body: {
    background: 'rgba(10,10,20,0.97)',
    padding: 24,
  },
  header: {
    background: 'rgba(10,10,20,0.97)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: '#fff',
  },
  wrapper: {
    background: 'rgba(10,10,20,0.97)',
  },
};

export const MODAL_TITLE_STYLE: CSSProperties = {
  color: '#fff',
  fontSize: 16,
  fontWeight: 600,
};

export const FORM_LABEL_STYLE = {
  label: {
    color: 'rgba(255,255,255,0.70)',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
};

// Scoped dark theme for forms inside dark modals/drawers.
// Apply via <ConfigProvider theme={darkFormTheme}> around Form inside any Modal
// that uses MODAL_STYLES. Do NOT apply this globally.
export const darkFormTheme = {
  // Checkbox has no component-level color token in antd v5.20 (its ComponentToken
  // interface is empty — confirmed against node_modules/antd/es/checkbox/style/index.d.ts).
  // It derives label/border color from the global colorText/colorBorder seed tokens instead,
  // so a Checkbox override has to happen here rather than under `components.Checkbox` (which
  // antd would silently ignore). This global colorText only reaches components that don't
  // already carry a more specific component-token override below (Form/Input/Select/
  // InputNumber/DatePicker keep their own colorText), so scoping it here is safe — it fixes
  // Checkbox.Group label text in AddEditBreakPeriodModal's "Select Calculations" step without
  // changing any already-themed component. Value matches the existing global
  // `.ant-modal-body` text color in src/index.css for consistency.
  token: {
    colorText: 'rgba(255,255,255,0.85)',
  },
  components: {
    Form: {
      labelColor: 'rgba(255,255,255,0.70)',
      labelFontSize: 12,
    },
    Input: {
      colorBgContainer: 'rgba(255,255,255,0.06)',
      colorBorder: 'rgba(255,255,255,0.12)',
      colorText: '#ffffff',
      colorTextPlaceholder: 'rgba(255,255,255,0.30)',
    },
    Select: {
      colorBgContainer: 'rgba(255,255,255,0.06)',
      colorBorder: 'rgba(255,255,255,0.12)',
      colorText: '#ffffff',
      colorBgElevated: 'rgba(15,22,40,0.98)',
      optionSelectedColor: '#F26522',
      optionSelectedBg: 'rgba(242,101,34,0.15)',
      colorTextPlaceholder: ThemePalette.colorWhiteFaint,
    },
    InputNumber: {
      colorBgContainer: 'rgba(255,255,255,0.06)',
      colorBorder: 'rgba(255,255,255,0.12)',
      colorText: '#ffffff',
    },
    DatePicker: {
      colorBgContainer: 'rgba(255,255,255,0.06)',
      colorBorder: 'rgba(255,255,255,0.12)',
      colorText: '#ffffff',
      colorTextDescription: ThemePalette.colorWhiteMuted,
      colorTextHeading: ThemePalette.colorWhite,
      colorIcon: ThemePalette.colorWhiteMuted,
      colorIconHover: ThemePalette.colorWhite,
      colorTextDisabled: ThemePalette.colorWhiteDisabled,
      controlItemBgHover: ThemePalette.colorWhiteSubtle,
      colorBgElevated: ThemePalette.colorModalSurfaceElevated,
      cellHoverBg: ThemePalette.colorWhiteSubtle,
      cellActiveWithRangeBg: ThemePalette.colorAccentSoft,
    },
    // Radio's own ComponentToken has no colorPrimary/colorPrimaryHover (confirmed against
    // node_modules/antd/es/radio/style/index.d.ts) — the checked-solid state (e.g. the
    // AddEditBreakPeriodModal Period/Event toggle) already derives buttonSolidCheckedBg from
    // the app's global colorPrimary (#F26522, set in src/theme/themes/light.ts) and
    // buttonSolidCheckedColor from colorTextLightSolid, both already correct without an
    // override here. The actual bug: buttonBg/buttonCheckedBg default to the global
    // colorBgContainer (untouched, still light-theme white — confirmed in the same style
    // file, `buttonBg: colorBgContainer`), while buttonColor's default (colorText) already
    // picks up this theme's colorText override above — so the UNCHECKED button was rendering
    // near-white text on a still-white background. Only buttonBg/buttonCheckedBg needed a
    // dark value; buttonColor is set explicitly too, matching this file's existing per-
    // component convention (Input/Select/etc. all redeclare colorText rather than rely on
    // the global token).
    Radio: {
      buttonBg: 'rgba(255,255,255,0.06)',
      buttonCheckedBg: 'rgba(255,255,255,0.06)',
      buttonColor: 'rgba(255,255,255,0.85)',
    },
  },
};
